'use client'

import { useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { de, enUS } from 'date-fns/locale'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { api } from '@/utils/trpc'

function formatPublishAt(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: locale !== 'de',
  }).format(date)
}

type PostSchedulePopoverProps = {
  postId: string
  publishAt: Date | null
  children: React.ReactNode // trigger element
  onSuccessAction?: () => void
}

export default function PostSchedulePopover({
  postId,
  publishAt,
  children,
  onSuccessAction,
}: PostSchedulePopoverProps) {
  const t = useTranslations('SocialPostDialog')
  const locale = useLocale()
  const dateLocale = locale === 'de' ? de : enUS
  const utils = api.useUtils()

  const [open, setOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    publishAt ?? undefined,
  )
  const [timeValue, setTimeValue] = useState<string>(
    publishAt
      ? `${String(publishAt.getHours()).padStart(2, '0')}:${String(publishAt.getMinutes()).padStart(2, '0')}`
      : '09:00',
  )

  const setScheduleMutation = api.instagramPosts.setSchedule.useMutation({
    onSuccess: (_, variables) => {
      utils.instagramPosts.get.invalidate({ id: postId })
      utils.instagramPosts.list.invalidate()
      setOpen(false)
      onSuccessAction?.()
      if (variables.publishAt) {
        toast(
          t('scheduledToast', {
            date: formatPublishAt(variables.publishAt as Date, locale),
          }),
        )
      } else {
        toast(t('scheduleRemovedToast'))
      }
    },
  })

  const handleSave = () => {
    if (!selectedDate) return
    const [hours, minutes] = timeValue.split(':').map(Number)
    const combined = new Date(selectedDate)
    combined.setHours(hours ?? 9, minutes ?? 0, 0, 0)
    setScheduleMutation.mutate({ id: postId, publishAt: combined })
  }

  const handleClear = () => {
    setScheduleMutation.mutate({ id: postId, publishAt: null })
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          locale={dateLocale}
        />
        <div className="flex flex-col gap-3 p-3 border-t border-border">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground w-16">
              {t('timeLabel')}
            </label>
            <input
              type="time"
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
              step={60}
              lang={locale === 'de' ? 'de-DE' : 'en-US'}
              className="flex h-8 flex-1 rounded-lg border border-input bg-transparent px-2 text-sm outline-none focus:ring-1 focus:ring-foreground/20 [&::-webkit-datetime-edit-ampm-field]:hidden"
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!selectedDate || setScheduleMutation.isPending}
              className="flex-1"
            >
              {t('save')}
            </Button>
            {publishAt && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClear}
                disabled={setScheduleMutation.isPending}
              >
                {t('clear')}
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
